import { action, makeObservable, observable, runInAction } from 'mobx';
import { ProviderClaimPayment } from '../../api/server/models/ProviderClaimPayment';
import { ProviderClearinghouseEraItem } from '../../api/server/models/ProviderClearinghouseEraItem';
import { amountOf, eobProviderAdjustments, eraProviderAdjustments, IProviderAdjustment } from '../../services/remits/remit-adjustments';
import { eobClaim, eraClaim, IRemitClaim } from '../../services/remits/remit-claims';
import { IRemit, IRemitClaimLink, IRemitQuery, IRemitsApi, RemitKind } from '../../services/remits/remits-api';

export type RemitFilters = Omit<IRemitQuery, 'limit' | 'offset'>;

/** A remit's claims, loaded once: for the list's Claims and Posted columns, and the expanded remit. */
export interface IRemitClaimsState {
    loading: boolean;
    error?: string;
    claims?: IRemitClaim[];
}

/** How many lookups run at once — each remit's claims, each EOB payment's claim (crosswalk §1: no count on the list). */
const LOOKUPS_AT_ONCE = 4;

/** Runs `task` over `items`, at most `LOOKUPS_AT_ONCE` at a time, until `stopped()` says the result is no longer wanted. */
const throttled = async <T>(items: T[], task: (item: T) => Promise<unknown>, stopped: () => boolean): Promise<void> => {
    const queue = [...items];
    const worker = async (): Promise<void> => {
        for (let next = queue.shift(); next !== undefined && !stopped(); next = queue.shift()) {
            await task(next);
        }
    };
    await Promise.all(Array.from({ length: LOOKUPS_AT_ONCE }, worker));
};

const sameFilters = (a: RemitFilters, b: RemitFilters): boolean => {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)] as Array<keyof RemitFilters>);
    return [...keys].every(key => (a[key] || undefined) === (b[key] || undefined));
};

/**
 * The Remits tab's state (FE-163), on either console: what differs — which endpoints — comes in as `api`.
 *
 * ## The list's Claims and Posted columns cost a call per row
 *
 * The list endpoints carry no claim count and no posted state (crosswalk §1), so each row on screen loads its own
 * claims: the ERA's items, the EOB's payments. They are kept, so expanding a row that has loaded is instant, and
 * fetched a few at a time so a page of twenty does not open twenty requests at once. A page that is left stops
 * loading; what it already fetched stays.
 */
export class RemitsStore {
    @observable public kind: RemitKind = 'ERA';
    @observable public filters: RemitFilters = {};
    @observable public pageIndex = 0;
    @observable public pageSize = 20;
    @observable public rows: IRemit[] = [];
    @observable public total = 0;
    /** Each tab's count, for its label: `EOB 3`. Unknown until asked. */
    @observable public counts: Partial<Record<RemitKind, number>> = {};
    @observable public loading = false;
    @observable public error: string | undefined;
    @observable public clearinghouses: Array<{ id: string; name: string; type?: string; created?: string }> = [];
    /** The clearinghouse list failed: the names fall back to ids, and the upload has nowhere to choose. */
    @observable public clearinghousesError = false;
    /**
     * The clearinghouse filter came back with another clearinghouse's ERAs. Sandbox ignored `filterClearinghouseId` when
     * first probed (2026-09-18); production honours it (2026-10-08). Checked on every page rather than trusted, so a
     * list labelled with one clearinghouse never silently holds the whole account (NS-3).
     */
    @observable public clearinghouseFilterIgnored = false;
    /** Filtered by clearinghouse, but some ERA on the page carries no clearinghouse id, so the filter cannot be checked. */
    @observable public clearinghouseFilterUnconfirmed = false;
    /** Keyed by remit id. */
    @observable public claims = new Map<string, IRemitClaimsState>();
    /** Claims behind payments, keyed by payment id: `null` once looked up and not found. */
    @observable public claimLinks = new Map<string, IRemitClaimLink | null>();

    public readonly api: IRemitsApi;
    private request = 0;
    /** The other tab's count is asked again only when what it counts changes, not on every page. */
    private countedFor: string | undefined;
    private linksPending = new Set<string>();

    constructor({ api }: { api: IRemitsApi }) {
        this.api = api;
        makeObservable(this);
    }

    /**
     * A fresh visit: no filters, first page, nothing cached. The page's inputs start empty, so the list behind them must
     * too — and a remit posted since the last visit must not show its old state.
     */
    @action
    public reset = (): void => {
        this.request++;
        this.kind = 'ERA';
        this.filters = {};
        this.pageIndex = 0;
        this.rows = [];
        this.total = 0;
        this.counts = {};
        this.error = undefined;
        this.loading = false;
        this.clearinghouseFilterIgnored = false;
        this.clearinghouseFilterUnconfirmed = false;
        this.countedFor = undefined;
        this.claims.clear();
        this.claimLinks.clear();
    };

    @action
    public setKind = (kind: RemitKind): void => {
        if (kind === this.kind) {
            return;
        }
        this.kind = kind;
        // The clearinghouse is an ERA filter; an EOB has none (crosswalk §1).
        this.filters = { ...this.filters, clearinghouseId: undefined };
        this.pageIndex = 0;
        // The other kind's columns must not be drawn over these rows while the new ones load.
        this.rows = [];
        void this.load();
    };

    @action
    public setFilters = (patch: Partial<RemitFilters>): void => {
        const next = { ...this.filters, ...patch };
        if (sameFilters(next, this.filters)) {
            return;
        }
        this.filters = next;
        this.pageIndex = 0;
        void this.load();
    };

    @action
    public setPage = (pageIndex: number, pageSize: number): void => {
        if (pageIndex === this.pageIndex && pageSize === this.pageSize && (this.rows.length || this.loading)) {
            return;
        }
        this.pageIndex = pageIndex;
        this.pageSize = pageSize;
        void this.load();
    };

    /** An EOB's patients, from the claims its payments settle (crosswalk §2: not on the payment). */
    public loadClaimLinks = async (claims: IRemitClaim[]): Promise<void> => {
        const pending = claims
            .map(claim => claim.paymentId)
            .filter((id): id is string => !!id && !this.claimLinks.has(id) && !this.linksPending.has(id));
        await throttled(pending, paymentId => this.claimLink(paymentId), () => false);
    };

    /** The claim behind a payment, looked up once. */
    public claimLink = async (paymentId: string): Promise<IRemitClaimLink | undefined> => {
        if (this.claimLinks.has(paymentId)) {
            return this.claimLinks.get(paymentId) || undefined;
        }
        this.linksPending.add(paymentId);
        try {
            const link = await this.api.claimForPayment(paymentId);
            runInAction(() => this.claimLinks.set(paymentId, link || null));
            return link;
        } catch {
            return undefined; // Not cached: a later click may succeed.
        } finally {
            this.linksPending.delete(paymentId);
        }
    };

    public loadClearinghouses = async (): Promise<void> => {
        try {
            const list = await this.api.listClearinghouses();
            runInAction(() => {
                this.clearinghouses = list;
                this.clearinghousesError = false;
            });
        } catch {
            runInAction(() => (this.clearinghousesError = true));
        }
    };

    public load = async (): Promise<void> => {
        const request = ++this.request;
        const kind = this.kind;
        const other: RemitKind = kind === 'ERA' ? 'EOB' : 'ERA';
        const query = { ...this.filters, limit: this.pageSize, offset: this.pageIndex * this.pageSize };
        const countKey = JSON.stringify({ kind, filters: this.filters });
        runInAction(() => {
            this.loading = true;
            this.error = undefined;
        });
        if (!this.clearinghouses.length) {
            void this.loadClearinghouses();
        }
        // The other tab's count, with the same filters, so its label is never stale. Decoration: a failure keeps the
        // last count rather than failing the list. An answer for filters that have since changed is dropped.
        if (this.countedFor !== countKey) {
            this.countedFor = countKey;
            this.api.listRemits(other, { ...query, clearinghouseId: undefined, limit: 1, offset: 0 }).then(
                page => {
                    if (this.countedFor === countKey) {
                        runInAction(() => (this.counts = { ...this.counts, [other]: page.total }));
                    }
                },
                () => {
                    if (this.countedFor === countKey) {
                        this.countedFor = undefined;
                    }
                },
            );
        }
        try {
            const page = await this.api.listRemits(kind, query);
            if (request !== this.request) {
                return;
            }
            runInAction(() => {
                this.rows = page.rows;
                this.total = page.total;
                this.counts = { ...this.counts, [kind]: page.total };
                this.clearinghouseFilterIgnored =
                    kind === 'ERA' &&
                    !!query.clearinghouseId &&
                    page.rows.some(row => !!row.clearinghouseId && row.clearinghouseId !== query.clearinghouseId);
                this.clearinghouseFilterUnconfirmed =
                    !this.clearinghouseFilterIgnored &&
                    kind === 'ERA' &&
                    !!query.clearinghouseId &&
                    page.rows.some(row => !row.clearinghouseId);
                this.loading = false;
            });
            void throttled(
                page.rows.filter(row => !this.claims.has(row.id)),
                row => this.loadClaims(row),
                () => request !== this.request,
            );
        } catch (err) {
            if (request !== this.request) {
                return;
            }
            runInAction(() => {
                this.rows = [];
                this.total = 0;
                this.error = err instanceof Error ? err.message : 'The remits could not be loaded.';
                this.loading = false;
            });
        }
    };

    public loadClaims = async (remit: IRemit): Promise<void> => {
        const known = this.claims.get(remit.id);
        if (known && (known.loading || known.claims)) {
            return;
        }
        runInAction(() => this.claims.set(remit.id, { loading: true }));
        try {
            const raw = await this.api.listClaims(remit);
            const claims =
                remit.kind === 'ERA'
                    ? (raw as ProviderClearinghouseEraItem[]).map(eraClaim)
                    : (raw as ProviderClaimPayment[]).map(eobClaim);
            runInAction(() => this.claims.set(remit.id, { loading: false, claims }));
        } catch (err) {
            runInAction(() =>
                this.claims.set(remit.id, {
                    loading: false,
                    error: err instanceof Error ? err.message : 'The claims on this remit could not be loaded.',
                }),
            );
        }
    };

    /** The remit's provider adjustments; undefined when an ERA's stored 835 cannot be read. */
    public providerAdjustments = (remit: IRemit): IProviderAdjustment[] | undefined =>
        remit.kind === 'ERA' ? eraProviderAdjustments(remit.x12) : eobProviderAdjustments(remit.eob?.providerAdjustments);

    public paidOf = (remit: IRemit): number => amountOf(remit.paidAmount ? { amount: remit.paidAmount } : undefined);

    public clearinghouseName = (id: string | undefined): string | undefined =>
        id ? this.clearinghouses.find(clearinghouse => clearinghouse.id === id)?.name : undefined;
}
